import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { HelpCircle, X } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { useProfileApi } from "../api/profile";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import AppFooter from "../components/AppFooter";

// Predefined service categories. Any value not in this list renders the
// "Other" branch and is editable as free text. Keep the list short — the
// goal is "common case in two taps", not exhaustive trade taxonomy.
const SERVICE_CATEGORIES = [
  { value: "handyman", label: "Handyman" },
  { value: "plumber", label: "Plumber" },
  { value: "electrician", label: "Electrician" },
  { value: "hvac", label: "HVAC" },
  { value: "painter", label: "Painter" },
  { value: "landscaper", label: "Landscaper" },
  { value: "cleaner", label: "Cleaner" },
];

const STANDARD_CATEGORY_VALUES = new Set(SERVICE_CATEGORIES.map((c) => c.value));

const EMPTY_FORM = {
  business_name: "",
  contractor_name: "",
  phone: "",
  email: "",
  address: "",
  license_number: "",
  tagline: "",
  // Tax defaults — flat in form, nested in payload (matches backend
  // ContractorProfile.default_tax TaxIn block).
  default_tax_enabled: false,
  default_tax_mode: "percentage",
  default_tax_value: "",
  default_tax_label: "Tax",
  invoice_footer_notes: "",
  payment_methods: "",
  // Default payment terms — flat in form, nested in payload.
  default_payment_terms_days: "",   // string for the number input
  default_payment_terms_label: "",  // optional override label
  // Service category — free text with a select for the common cases.
  service_category: "",
  // Logo — read-only on the form; written via uploadLogo/deleteLogo. The
  // PUT /profile excludes this field so identity saves can't blank it.
  logo: null,
};

function profileToForm(p) {
  if (!p) return EMPTY_FORM;
  // Logo-only profiles are a valid intermediate state (Step C upsert): user
  // can upload the logo before filling in business_name + contractor_name.
  // We populate the logo here but leave required identity fields empty for
  // the user to complete — the save validator will block any save until
  // they're filled.
  const dt = p.default_tax || {};
  const dpt = p.default_payment_terms || {};
  return {
    business_name: p.business_name || "",
    contractor_name: p.contractor_name || "",
    phone: p.phone || "",
    email: p.email || "",
    address: p.address || "",
    license_number: p.license_number || "",
    tagline: p.tagline || "",
    default_tax_enabled: !!dt.enabled,
    default_tax_mode: dt.mode === "flat" ? "flat" : "percentage",
    default_tax_value: dt.value != null ? String(dt.value) : "",
    default_tax_label: dt.label || "Tax",
    invoice_footer_notes: p.invoice_footer_notes || "",
    payment_methods: p.payment_methods || "",
    default_payment_terms_days: dpt.days != null ? String(dpt.days) : "",
    default_payment_terms_label: dpt.label || "",
    service_category: p.service_category || "",
    logo: p.logo || null,
  };
}

function formToPayload(f) {
  const trim = (s) => (s ?? "").trim();
  const nullIf = (s) => {
    const t = trim(s);
    return t === "" ? null : t;
  };

  // Tax defaults → nested TaxIn block.
  let tax_value = null;
  if (f.default_tax_enabled && trim(f.default_tax_value) !== "") {
    const n = Number(f.default_tax_value);
    if (Number.isFinite(n) && n > 0) {
      tax_value = +n.toFixed(4);
    }
  }
  const default_tax = {
    enabled: !!f.default_tax_enabled && tax_value !== null,
    mode:
      f.default_tax_enabled && tax_value !== null ? f.default_tax_mode : null,
    value: tax_value,
    label: nullIf(f.default_tax_label) || "Tax",
  };

  // Payment terms → nested or null if blank. days=0 is valid ("Due on receipt").
  let default_payment_terms = null;
  const daysRaw = trim(f.default_payment_terms_days);
  if (daysRaw !== "") {
    const n = parseInt(daysRaw, 10);
    if (Number.isFinite(n) && n >= 0) {
      default_payment_terms = {
        days: n,
        label: nullIf(f.default_payment_terms_label),
      };
    }
  }

  return {
    business_name: trim(f.business_name),
    contractor_name: trim(f.contractor_name),
    phone: nullIf(f.phone),
    email: nullIf(f.email),
    address: nullIf(f.address),
    license_number: nullIf(f.license_number),
    tagline: nullIf(f.tagline),
    default_tax,
    invoice_footer_notes: nullIf(f.invoice_footer_notes),
    payment_methods: nullIf(f.payment_methods),
    default_payment_terms,
    service_category: nullIf(f.service_category),
    // logo NOT included — backend PUT excludes it; managed by /profile/logo.
  };
}

export default function Settings() {
  const navigate = useNavigate();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { fetchProfile, saveProfile, uploadLogo, deleteLogo } = useProfileApi();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // Logo upload/delete state. Distinct from `error` so a logo failure
  // doesn't crowd the form-save error region.
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState("");

  useEffect(() => {
    if (!authLoaded) return;
    let mounted = true;
    fetchProfile()
      .then((p) => {
        if (mounted) setForm(profileToForm(p));
      })
      .catch((e) => {
        if (mounted) setError(e?.message || String(e));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [authLoaded, isSignedIn, fetchProfile]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Logo upload — uses the multipart endpoint, replaces the entire form
  // state on success so the latest server-side profile shape wins.
  const onLogoUpload = async (file) => {
    if (!file) return;
    setLogoError("");
    // Pre-flight checks mirror the backend's so we fail fast and friendly.
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Logo must be a PNG or JPG file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(
        `Logo is ${(file.size / 1024 / 1024).toFixed(2)}MB. Max is 2MB.`,
      );
      return;
    }
    setLogoBusy(true);
    try {
      const updated = await uploadLogo(file);
      setForm(profileToForm(updated));
    } catch (e) {
      setLogoError(e?.response?.data?.detail || e?.message || "Upload failed.");
    } finally {
      setLogoBusy(false);
    }
  };

  const onLogoRemove = async () => {
    if (!form.logo) return;
    setLogoError("");
    setLogoBusy(true);
    try {
      const updated = await deleteLogo();
      setForm(profileToForm(updated));
    } catch (e) {
      setLogoError(e?.response?.data?.detail || e?.message || "Remove failed.");
    } finally {
      setLogoBusy(false);
    }
  };

  // Client-side validation mirrors the backend rules so we show a useful
  // message before sending.
  const validate = () => {
    if (!form.business_name.trim()) return "Business name is required.";
    if (!form.contractor_name.trim()) return "Your name is required.";
    if (!form.phone.trim() && !form.email.trim()) {
      return "Add at least one of phone or email so clients can reach you.";
    }
    if (form.default_tax_enabled) {
      const raw = form.default_tax_value.trim();
      if (raw === "") return "Enter a tax value, or turn the default off.";
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return "Tax value must be a positive number.";
      if (form.default_tax_mode === "percentage" && n > 100) {
        return "Tax percentage must be 100 or less.";
      }
    }
    const daysRaw = form.default_payment_terms_days.trim();
    if (daysRaw !== "") {
      const n = parseInt(daysRaw, 10);
      if (!Number.isFinite(n) || String(n) !== daysRaw) {
        return "Payment terms days must be a whole number.";
      }
      if (n < 0 || n > 365) {
        return "Payment terms days must be between 0 (due on receipt) and 365.";
      }
    }
    return "";
  };

  const onSave = async () => {
    setError("");
    setSaved(false);
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      const saved_ = await saveProfile(payload);
      setForm(profileToForm(saved_));
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell testId="settings-page" padding="20px 18px 40px" maxWidth={680}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <BackLink to="/" label="Home" testId="settings-back-link" />
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <AuthChip />
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: COLORS.fainter,
              textTransform: "uppercase",
            }}
          >
            PROFILE
          </span>
        </div>
      </header>

      <h1
        style={{
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontStyle: "italic",
          fontSize: 30,
          margin: "0 0 6px 0",
        }}
      >
        Your business<span style={{ color: COLORS.yellow }}>.</span>
      </h1>
      <p
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          color: COLORS.faint,
          letterSpacing: "0.05em",
          margin: "0 0 22px 0",
        }}
      >
        // shown at the top of every invoice
      </p>

      {authLoaded && !isSignedIn && (
        <div
          data-testid="settings-anonymous-banner"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            color: COLORS.faint,
            letterSpacing: "0.05em",
            lineHeight: 1.6,
            padding: "10px 0",
            borderTop: `1px solid ${COLORS.rule || "#3a362e"}`,
            borderBottom: `1px solid ${COLORS.rule || "#3a362e"}`,
            margin: "0 0 22px 0",
          }}
        >
          // Anonymous mode — settings are saved to your browser only. Sign in
          to save permanently.
        </div>
      )}

      {loading ? (
        <div
          data-testid="settings-loading"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            paddingTop: 40,
            textAlign: "center",
          }}
        >
          loading…
        </div>
      ) : (
        <>
          <BrandSection
            logo={form.logo}
            busy={logoBusy}
            error={logoError}
            isSignedIn={!!isSignedIn}
            onUpload={onLogoUpload}
            onRemove={onLogoRemove}
          />

          <Section title="Required">
            <FieldInput
              testid="settings-business-name"
              label="Business name"
              value={form.business_name}
              onChange={set("business_name")}
              placeholder="e.g. Smith Plumbing"
              required
            />
            <FieldInput
              testid="settings-contractor-name"
              label="Your name"
              value={form.contractor_name}
              onChange={set("contractor_name")}
              placeholder="e.g. Mike Johnson"
              required
            />
            <FieldInput
              testid="settings-phone"
              label="Phone"
              value={form.phone}
              onChange={set("phone")}
              placeholder="e.g. (555) 012-3456"
              inputMode="tel"
            />
            <FieldInput
              testid="settings-email"
              label="Email"
              value={form.email}
              onChange={set("email")}
              placeholder="e.g. mike@smithplumbing.com"
              inputMode="email"
            />
            <Helper>One of phone or email is required so clients can reach you.</Helper>
          </Section>

          <Section title="Identity">
            <FieldInput
              testid="settings-tagline"
              label="Tagline"
              value={form.tagline}
              onChange={set("tagline")}
              placeholder="e.g. Licensed plumber · family-owned since 1998"
            />
            <FieldInput
              testid="settings-address"
              label="Address"
              value={form.address}
              onChange={set("address")}
              placeholder="Optional"
            />
            <FieldInput
              testid="settings-license"
              label="License #"
              value={form.license_number}
              onChange={set("license_number")}
              placeholder="Optional"
            />
          </Section>

          <Section title="Invoice Defaults">
            <TaxDefaultsField
              enabled={form.default_tax_enabled}
              mode={form.default_tax_mode}
              value={form.default_tax_value}
              label={form.default_tax_label}
              onToggleEnabled={(v) =>
                setForm((f) => ({ ...f, default_tax_enabled: v }))
              }
              onSwitchMode={(m) =>
                // Match the Review-screen pattern: clear value when modes
                // flip to avoid carrying over a number that means something
                // different in the new mode.
                setForm((f) => ({ ...f, default_tax_mode: m, default_tax_value: "" }))
              }
              onValueChange={(v) =>
                setForm((f) => ({ ...f, default_tax_value: v.replace(/[^0-9.]/g, "") }))
              }
              onLabelChange={(v) => setForm((f) => ({ ...f, default_tax_label: v }))}
            />
            <Helper>
              Pre-populates the tax section on every new invoice. You can
              still toggle or edit it per-invoice on the Review screen.
            </Helper>
            <FieldInput
              testid="settings-payment-methods"
              label="Payment methods"
              value={form.payment_methods}
              onChange={set("payment_methods")}
              placeholder="e.g. Venmo or check accepted"
            />
            <FieldTextarea
              testid="settings-footer-notes"
              label="Invoice notes / payment terms"
              value={form.invoice_footer_notes}
              onChange={set("invoice_footer_notes")}
              placeholder="e.g., Payment due within 14 days. Thank you for your business."
              maxChars={300}
            />
            <Helper>
              Prints as a small note at the bottom of every invoice PDF.
            </Helper>
            <PaymentTermsField
              days={form.default_payment_terms_days}
              label={form.default_payment_terms_label}
              onChangeDays={set("default_payment_terms_days")}
              onChangeLabel={set("default_payment_terms_label")}
            />
            <Helper>
              Drives the "Due by" date on every invoice. Use 0 for "Due on
              receipt", or leave blank to skip the due-by line entirely.
            </Helper>
            <ServiceCategoryField
              value={form.service_category}
              onChange={(v) =>
                setForm((f) => ({ ...f, service_category: v }))
              }
            />
            <Helper>
              Drives templates and category suggestions on Review. Use Other
              to type a trade not in the list.
            </Helper>
          </Section>

          {error && (
            <div
              data-testid="settings-error"
              role="alert"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${COLORS.red}`,
                backgroundColor: "rgba(229,75,60,0.08)",
                color: "#ffb3ab",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                lineHeight: 1.5,
                marginTop: 14,
              }}
            >
              {typeof error === "string" ? error : JSON.stringify(error)}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            <button
              data-testid="settings-save-btn"
              onClick={onSave}
              disabled={saving}
              style={{
                flex: 1,
                padding: "15px 18px",
                borderRadius: 999,
                border: "none",
                backgroundColor: saved ? COLORS.ok : COLORS.yellow,
                color: "#0f0e0c",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                cursor: saving ? "progress" : "pointer",
                boxShadow: "0 24px 60px -18px rgba(232,220,45,0.5)",
                opacity: saving ? 0.7 : 1,
                transition: "background-color 240ms ease",
              }}
            >
              {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
            </button>
            <button
              data-testid="settings-cancel-btn"
              onClick={() => navigate(-1)}
              style={{
                padding: "15px 18px",
                borderRadius: 999,
                border: `1px solid ${COLORS.rule}`,
                backgroundColor: "transparent",
                color: COLORS.faint,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
      <AppFooter />
    </PageShell>
  );
}

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 22 }}>
    <div
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: COLORS.faint,
        marginBottom: 10,
        paddingBottom: 8,
        borderBottom: `1px dashed ${COLORS.rule}`,
      }}
    >
      {title}
    </div>
    {children}
  </section>
);

const baseInputStyle = {
  width: "100%",
  backgroundColor: COLORS.panel,
  border: `1px solid ${COLORS.rule}`,
  borderRadius: 10,
  padding: "11px 12px",
  color: COLORS.text,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 160ms ease, background-color 160ms ease",
};

const FieldInput = ({ testid, label, value, onChange, placeholder, required, inputMode, suffix }) => (
  <label
    style={{
      display: "block",
      marginBottom: 12,
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 10,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: COLORS.faint,
    }}
  >
    <div style={{ marginBottom: 6 }}>
      {label}
      {required && (
        <span style={{ color: COLORS.yellow, marginLeft: 4 }}>*</span>
      )}
    </div>
    <div style={{ position: "relative" }}>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        style={{
          ...baseInputStyle,
          paddingRight: suffix ? 34 : 12,
          textTransform: "none",
          letterSpacing: "normal",
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = COLORS.yellow)}
        onBlur={(e) => (e.currentTarget.style.borderColor = COLORS.rule)}
      />
      {suffix && (
        <span
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: COLORS.faint,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  </label>
);

const FieldTextarea = ({ testid, label, value, onChange, placeholder, maxChars }) => {
  const len = (value || "").length;
  const over = maxChars != null && len > maxChars;
  return (
  <label
    style={{
      display: "block",
      marginBottom: 12,
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 10,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: COLORS.faint,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 6,
      }}
    >
      <span>{label}</span>
      {maxChars != null && (
        <span
          data-testid={testid ? `${testid}-counter` : undefined}
          style={{
            fontSize: 10,
            letterSpacing: "0.08em",
            color: over ? COLORS.warn : COLORS.faint,
          }}
        >
          {len} / {maxChars}
        </span>
      )}
    </div>
    <textarea
      data-testid={testid}
      rows={3}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        ...baseInputStyle,
        resize: "vertical",
        minHeight: 66,
        lineHeight: 1.5,
        textTransform: "none",
        letterSpacing: "normal",
        fontFamily: "'Inter', sans-serif",
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = COLORS.yellow)}
      onBlur={(e) => (e.currentTarget.style.borderColor = COLORS.rule)}
    />
  </label>
  );
};

const Helper = ({ children }) => (
  <div
    style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 10,
      color: COLORS.faint,
      lineHeight: 1.5,
      margin: "-4px 0 14px",
      letterSpacing: "0.02em",
    }}
  >
    // {children}
  </div>
);

// Tax-defaults field: master enable + mode toggle + value/label inputs.
// Mirrors the Review-screen tax block — same data model, same affordances,
// so the per-invoice override on Review feels like an extension of these
// defaults rather than a parallel system.
const TaxDefaultsField = ({
  enabled,
  mode,
  value,
  label,
  onToggleEnabled,
  onSwitchMode,
  onValueChange,
  onLabelChange,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  return (
    <label
      style={{
        display: "block",
        marginBottom: 12,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: COLORS.faint,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Default tax
          <span style={{ position: "relative", display: "inline-flex" }}>
            <button
              type="button"
              data-testid="settings-tax-tooltip-btn"
              aria-label="Tax help"
              onClick={(e) => {
                e.preventDefault();
                setTooltipOpen((v) => !v);
              }}
              onBlur={() => setTimeout(() => setTooltipOpen(false), 120)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "help",
                color: COLORS.faint,
                display: "inline-flex",
              }}
            >
              <HelpCircle size={12} />
            </button>
            {tooltipOpen && (
              <span
                data-testid="settings-tax-tooltip"
                role="tooltip"
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  zIndex: 2,
                  width: 240,
                  padding: "8px 10px",
                  borderRadius: 8,
                  backgroundColor: "#1a1917",
                  border: `1px solid ${COLORS.rule}`,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  letterSpacing: "normal",
                  textTransform: "none",
                  color: "#d6d2c3",
                  lineHeight: 1.45,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                }}
              >
                Flat $ adds a fixed amount. Percentage % multiplies the
                subtotal. Consult your accountant for jurisdiction rules.
              </span>
            )}
          </span>
        </span>
        <SettingsToggle
          on={enabled}
          onChange={onToggleEnabled}
          testid="settings-tax-enabled"
        />
      </div>
      {enabled && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <SegmentBtn
              active={mode === "flat"}
              onClick={() => onSwitchMode("flat")}
              testid="settings-tax-mode-flat"
            >
              Flat $
            </SegmentBtn>
            <SegmentBtn
              active={mode === "percentage"}
              onClick={() => onSwitchMode("percentage")}
              testid="settings-tax-mode-pct"
            >
              Percentage %
            </SegmentBtn>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                ...baseInputStyle,
                padding: "11px 12px",
                flex: "0 0 130px",
              }}
            >
              {mode === "flat" && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 13,
                    color: COLORS.faint,
                  }}
                >
                  $
                </span>
              )}
              <input
                data-testid="settings-tax-value"
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => onValueChange(e.target.value)}
                placeholder={mode === "flat" ? "0.00" : "8.25"}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: COLORS.text,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 14,
                  textAlign: "right",
                  padding: 0,
                  textTransform: "none",
                  letterSpacing: "normal",
                }}
              />
              {mode === "percentage" && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 13,
                    color: COLORS.faint,
                  }}
                >
                  %
                </span>
              )}
            </div>
            <input
              data-testid="settings-tax-label"
              type="text"
              value={label}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder="Tax"
              maxLength={24}
              style={{
                ...baseInputStyle,
                flex: 1,
                textTransform: "none",
                letterSpacing: "normal",
                fontFamily: "'Inter', sans-serif",
              }}
            />
          </div>
        </div>
      )}
    </label>
  );
};

const SettingsToggle = ({ on, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    data-testid={testid}
    onClick={() => onChange(!on)}
    style={{
      width: 36,
      height: 20,
      borderRadius: 999,
      border: `1px solid ${on ? COLORS.yellow : COLORS.rule}`,
      backgroundColor: on ? COLORS.yellow : "transparent",
      position: "relative",
      cursor: "pointer",
      transition: "background-color 140ms ease, border-color 140ms ease",
      padding: 0,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: on ? 18 : 2,
        width: 14,
        height: 14,
        borderRadius: "50%",
        backgroundColor: on ? "#0f0e0c" : COLORS.faint,
        transition: "left 160ms ease, background-color 140ms ease",
      }}
    />
  </button>
);

const SegmentBtn = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    style={{
      flex: 1,
      padding: "10px 12px",
      borderRadius: 8,
      border: `1px solid ${active ? COLORS.yellow : COLORS.rule}`,
      backgroundColor: active ? COLORS.yellow : "transparent",
      color: active ? "#0f0e0c" : COLORS.faint,
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 11,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      cursor: "pointer",
      transition: "color 140ms ease, background-color 140ms ease, border-color 140ms ease",
    }}
  >
    {children}
  </button>
);

/* ========================================================================== */
/*  BrandSection — logo upload (drag-drop + browse), preview, replace, remove */
/*  Signed-in only: the backend POST /profile/logo requires auth, and we don't*/
/*  base64-stash binary blobs in sessionStorage. Anonymous users see a hint.  */
/* ========================================================================== */

const BrandSection = ({ logo, busy, error, isSignedIn, onUpload, onRemove }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    if (!busy) fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    // Reset so picking the same file twice still fires onChange.
    e.target.value = "";
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!busy) setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  if (!isSignedIn) {
    return (
      <Section title="Brand">
        <div
          data-testid="settings-brand-anonymous"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            color: COLORS.faint,
            letterSpacing: "0.04em",
            lineHeight: 1.6,
            padding: "10px 0",
          }}
        >
          // Sign in to upload a logo. Anonymous mode keeps everything in
          your browser, and logos need a backend slot to survive a tab close.
        </div>
      </Section>
    );
  }

  const hasLogo = !!logo;
  const previewSrc = hasLogo
    ? `data:${logo.mime_type};base64,${logo.data}`
    : null;

  return (
    <Section title="Brand">
      <div
        style={{
          display: "flex",
          gap: 14,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div
          data-testid="settings-logo-drop-zone"
          onClick={openPicker}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          role="button"
          aria-label={hasLogo ? "Replace logo" : "Upload logo"}
          tabIndex={busy ? -1 : 0}
          style={{
            width: 140,
            height: 140,
            borderRadius: 12,
            border: `1.5px dashed ${
              dragOver ? COLORS.yellow : COLORS.rule
            }`,
            backgroundColor: hasLogo ? "#1f1c17" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: busy ? "progress" : "pointer",
            transition: "border-color 160ms ease, background-color 160ms ease",
            overflow: "hidden",
            padding: hasLogo ? 8 : 12,
            boxSizing: "border-box",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {hasLogo ? (
            <img
              data-testid="settings-logo-preview"
              src={previewSrc}
              alt="Logo preview"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <span
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 10,
                color: COLORS.faint,
                letterSpacing: "0.08em",
                textAlign: "center",
                lineHeight: 1.5,
                padding: "0 8px",
              }}
            >
              Drop logo here,
              <br />
              or click to browse
            </span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            flex: 1,
            minWidth: 160,
          }}
        >
          {hasLogo && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                data-testid="settings-logo-replace"
                onClick={openPicker}
                disabled={busy}
                style={{
                  background: "transparent",
                  border: `1px solid ${COLORS.rule}`,
                  color: COLORS.text,
                  padding: "8px 14px",
                  borderRadius: 999,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                Replace
              </button>
              <button
                type="button"
                data-testid="settings-logo-remove"
                onClick={onRemove}
                disabled={busy}
                style={{
                  background: "transparent",
                  border: `1px solid ${COLORS.rule}`,
                  color: "#ffb3ab",
                  padding: "8px 14px",
                  borderRadius: 999,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <X size={12} /> Remove
              </button>
            </div>
          )}
          {busy && (
            <div
              data-testid="settings-logo-status"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                color: COLORS.faint,
                letterSpacing: "0.04em",
              }}
            >
              // uploading…
            </div>
          )}
          {error && (
            <div
              data-testid="settings-logo-error"
              role="alert"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                color: "#ffb3ab",
                letterSpacing: "0.04em",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onFileChange}
          style={{ display: "none" }}
          data-testid="settings-logo-file-input"
        />
      </div>
      <Helper>PNG or JPG. At least 100×100 pixels. Max 2MB.</Helper>
    </Section>
  );
};

/* ========================================================================== */
/*  PaymentTermsField — days + optional label, side by side                    */
/* ========================================================================== */

const PaymentTermsField = ({ days, label, onChangeDays, onChangeLabel }) => (
  <label
    style={{
      display: "block",
      marginBottom: 12,
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 10,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: COLORS.faint,
    }}
  >
    <div style={{ marginBottom: 6 }}>Default payment terms</div>
    <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          ...baseInputStyle,
          padding: "0 12px",
          flex: "0 0 130px",
        }}
      >
        <input
          data-testid="settings-payment-terms-days"
          type="text"
          inputMode="numeric"
          value={days}
          onChange={(e) =>
            onChangeDays({
              target: { value: e.target.value.replace(/[^0-9]/g, "") },
            })
          }
          placeholder="15"
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: COLORS.text,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 14,
            textAlign: "right",
            padding: "11px 0",
            textTransform: "none",
            letterSpacing: "normal",
          }}
        />
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            textTransform: "none",
            letterSpacing: "normal",
          }}
        >
          days
        </span>
      </div>
      <input
        data-testid="settings-payment-terms-label"
        type="text"
        value={label}
        onChange={onChangeLabel}
        placeholder="Optional label (e.g. Net 15)"
        maxLength={32}
        style={{
          ...baseInputStyle,
          flex: 1,
          textTransform: "none",
          letterSpacing: "normal",
          fontFamily: "'Inter', sans-serif",
        }}
      />
    </div>
  </label>
);

/* ========================================================================== */
/*  ServiceCategoryField — select with Other-as-free-text fallback             */
/* ========================================================================== */

const ServiceCategoryField = ({ value, onChange }) => {
  const isStandard = STANDARD_CATEGORY_VALUES.has(value);
  // `otherMode` tracks UI shape: which row of the select is currently shown.
  // value is the single source of truth for what gets persisted; otherMode
  // disambiguates "user clicked Other but hasn't typed yet" from "value is
  // empty because user picked the empty prompt option."
  const [otherMode, setOtherMode] = useState(value !== "" && !isStandard);

  // Resync when the form gets repopulated externally (profile reload).
  useEffect(() => {
    if (isStandard) setOtherMode(false);
    else if (value !== "") setOtherMode(true);
  }, [value, isStandard]);

  const selectValue = otherMode ? "other" : isStandard ? value : "";

  return (
    <label
      style={{
        display: "block",
        marginBottom: 12,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 10,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: COLORS.faint,
      }}
    >
      <div style={{ marginBottom: 6 }}>Service category</div>
      <select
        data-testid="settings-service-category-select"
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "other") {
            setOtherMode(true);
            // Clear any standard value so the Other input starts fresh.
            // (If they were already typing in Other, value stays.)
            if (isStandard) onChange("");
          } else {
            setOtherMode(false);
            onChange(v); // "" or a standard value
          }
        }}
        style={{
          ...baseInputStyle,
          textTransform: "none",
          letterSpacing: "normal",
          fontFamily: "'Inter', sans-serif",
          cursor: "pointer",
          appearance: "auto",
        }}
      >
        <option value="">Select category…</option>
        {SERVICE_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
        <option value="other">Other (specify)</option>
      </select>
      {otherMode && (
        <input
          data-testid="settings-service-category-other"
          type="text"
          value={isStandard ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. roofer, mason, locksmith"
          maxLength={32}
          style={{
            ...baseInputStyle,
            marginTop: 8,
            textTransform: "none",
            letterSpacing: "normal",
            fontFamily: "'Inter', sans-serif",
          }}
        />
      )}
    </label>
  );
};
